import { ascend, prop, sortWith } from "ramda";
import { type FC, type ReactNode, useMemo } from "react";
import { Item } from "react-stately";

import { useDataAttributes } from "../../../hooks/useDataAttributes.ts";
import { Icon } from "../../Icon/Icon.tsx";
import { VStack } from "../../Stack/VStack.tsx";
import { usePanelLayoutStore } from "../stores/panel-store.ts";
import style from "../TabPanel.module.css";
import type { Tab as TabType } from "../types.ts";
import { PanelTabs } from "./PanelTabs.tsx";

const sortByPosition = sortWith<TabType>([ascend(prop("position"))]);

export interface FixedPanelProps {
  /** If props.closedSize != (0 || undefined) */
  willNeverBeHidden: boolean;
  tabsActions?: ReactNode;
  openOnTabPress?: boolean;
}

export const FixedPanel: FC<FixedPanelProps> = (props) => {
  const tabs = usePanelLayoutStore((state) => state.tabs);
  const orderedTabs = useMemo(() => sortByPosition(tabs), [tabs]);
  const showFixedPanel = usePanelLayoutStore((state) => state.showFixedPanel);
  const setActiveTabId = usePanelLayoutStore((state) => state.setActiveTabId);
  const activeTabId = usePanelLayoutStore((state) => state.activeTabId);
  const togglePanel = usePanelLayoutStore((state) => state.togglePanel);

  const dataAttributes = useDataAttributes(props);

  if ((!showFixedPanel && !props.willNeverBeHidden) || tabs.length === 0) {
    return null;
  }

  return (
    <PanelTabs
      items={orderedTabs}
      onSelectionChange={(tabId) => {
        // We need to do it here too, because `activeTabId` will not change if it's the same tab
        // Here the callback will be called anyways
        if (props.openOnTabPress && !showFixedPanel && activeTabId != null) {
          togglePanel();
        }

        setActiveTabId(tabId);
      }}
      selectedKey={activeTabId}
      tabsActions={props.tabsActions}
      {...dataAttributes}
    >
      {(tab) => (
        <Item
          key={tab.id}
          title={
            tab.showTitle ? (
              <VStack spacing={1} style={{ alignItems: "center", fontSize: 9, lineHeight: "11px", fontWeight: 450 }}>
                <Icon icon={tab.icon} aria-hidden="true" className={style.icon} />
                {tab.title}
              </VStack>
            ) : (
              <Icon icon={tab.icon} aria-label={tab.title} className={style.icon} />
            )
          }
          textValue={tab.title}
        >
          {tab.children}
        </Item>
      )}
    </PanelTabs>
  );
};
